import { useState } from 'react';
import {
  DeleteOutlined,
  DownOutlined,
  ExperimentOutlined,
  LogoutOutlined,
  MailOutlined,
  StopOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Flex, Input, Modal, Select, Tooltip, Typography } from 'antd';
import { ROLE_DESCRIPTION, ROLE_LABEL, ROLES, type Role } from '@titularis/api-client';
import { api } from '../../api';
import { useSession } from '../../auth';
import { ExclusaoDaConta } from './ExclusaoDaConta';

/**
 * O que se pode fazer com uma conta, reunido onde há contexto para decidir.
 *
 * **As ações moravam em telas diferentes ou não existiam.** Trocar o papel era
 * um seletor dentro de uma linha da tabela de Administradores; excluir era um
 * botão ao lado dele; conceder acesso era outra tela. Reenviar a confirmação de
 * e-mail, suspender e encerrar sessões **não existiam** - e as três são o que
 * uma semana de lançamento pede, porque o degrau que mais segura gente é a
 * caixa de entrada e o risco que mais assusta é a conta compartilhada.
 *
 * A hierarquia visual segue a frequência e o risco: o que se faz todo dia fica
 * à vista, o que se faz uma vez por mês fica no menu, e o que não tem volta fica
 * no fim dele, em vermelho, atrás de uma confirmação que exige digitar o e-mail.
 *
 * **Nada aqui autoriza nada** (G-POLICY): as rotas recusam quem não é Dono, e
 * cada uma delas grava na trilha o que foi feito e por quem. Esconder o botão só
 * evita oferecer o que vai falhar.
 */
export function AcoesDaConta({
  pessoa,
}: {
  pessoa: {
    id: string;
    nome: string;
    email: string;
    papel: string;
    suspensa: boolean;
    emailConfirmado: boolean;
    contaDeTeste: boolean;
  };
}) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { data: sessao } = useSession();
  const [excluindo, setExcluindo] = useState(false);
  const [suspendendo, setSuspendendo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const ehVoce = pessoa.id === sessao?.user.id;

  const atualizar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['pessoa', pessoa.id] });
    await queryClient.invalidateQueries({ queryKey: ['pessoas'] });
    await queryClient.invalidateQueries({ queryKey: ['resumo-das-pessoas'] });
  };

  /*
   * Marcar como teste mexe em toda contagem da seção - o funil e a aba Origem
   * contam só alunos de verdade -, então o que se invalida vai além da ficha.
   */
  const marcarTeste = useMutation({
    mutationFn: (teste: boolean) => api.setPersonTestAccount(pessoa.id, teste),
    onSuccess: async (_resposta, teste) => {
      message.success(
        teste
          ? 'Marcada como conta de teste: saiu do funil e das métricas.'
          : 'Deixou de ser conta de teste: voltou ao funil e às métricas.',
      );
      await atualizar();
      await queryClient.invalidateQueries({ queryKey: ['funil-por-origem'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const trocarPapel = useMutation({
    mutationFn: (papel: Role) => api.setPersonRole(pessoa.id, papel),
    onSuccess: async () => {
      message.success('Nível de acesso atualizado.');
      await atualizar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const reenviar = useMutation({
    mutationFn: () => api.resendVerification(pessoa.id),
    onSuccess: (resposta) => message.success(`Confirmação reenviada para ${resposta.email}.`),
    onError: (erro: Error) => message.error(erro.message),
  });

  const suspender = useMutation({
    mutationFn: () => api.suspendPerson(pessoa.id, motivo.trim()),
    onSuccess: async () => {
      message.success('Conta suspensa.');
      setSuspendendo(false);
      setMotivo('');
      await atualizar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const reativar = useMutation({
    mutationFn: () => api.unsuspendPerson(pessoa.id),
    onSuccess: async () => {
      message.success('Conta reativada.');
      await atualizar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const encerrar = useMutation({
    mutationFn: () => api.revokePersonSessions(pessoa.id),
    onSuccess: async (resposta) => {
      message.success(`${resposta.encerradas} sessão(ões) encerrada(s).`);
      await atualizar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  function confirmarEncerramento() {
    modal.confirm({
      title: 'Encerrar todas as sessões?',
      content: `${pessoa.nome} cai para fora em todos os aparelhos e precisa entrar de novo. A conta continua valendo.`,
      okText: 'Encerrar',
      cancelText: 'Cancelar',
      onOk: () => encerrar.mutateAsync(),
    });
  }

  return (
    <Flex gap={8} wrap align="center">
      {/*
        O seletor de nível fica à vista, e não no menu: é a ação mais frequente
        sobre uma conta da casa, e é a única que se lê como estado - quem abre a
        ficha do Róger quer ver o nível dele mesmo sem ir mudar nada.

        Rebaixar a própria conta trancaria quem opera para fora do backoffice, e
        por isso a própria conta mostra o nível sem poder trocá-lo. A rota recusa
        de qualquer jeito; isto evita oferecer o que vai falhar.
      */}
      <Tooltip title={ehVoce ? 'Você não pode mudar o próprio nível de acesso.' : undefined}>
        <Select<Role>
          style={{ minWidth: 230 }}
          value={pessoa.papel as Role}
          disabled={ehVoce}
          loading={trocarPapel.isPending}
          onChange={(papel) => trocarPapel.mutate(papel)}
          options={ROLES.map((papel) => ({
            value: papel,
            label: ROLE_LABEL[papel],
            title: ROLE_DESCRIPTION[papel],
          }))}
        />
      </Tooltip>

      {/*
        Só aparece para quem não confirmou. Um botão de reenviar confirmação
        numa conta já confirmada é um botão que só sabe dar erro - e a rota o
        recusa, justamente por isso.
      */}
      {!pessoa.emailConfirmado ? (
        <Button
          icon={<MailOutlined />}
          loading={reenviar.isPending}
          onClick={() => reenviar.mutate()}
        >
          Reenviar confirmação
        </Button>
      ) : null}

      {pessoa.suspensa ? (
        <Button
          icon={<UndoOutlined />}
          loading={reativar.isPending}
          onClick={() => reativar.mutate()}
        >
          Reativar conta
        </Button>
      ) : null}

      <Dropdown
        menu={{
          items: [
            {
              key: 'sessoes',
              icon: <LogoutOutlined />,
              label: 'Encerrar sessões',
              onClick: confirmarEncerramento,
            },
            /*
              No menu, e não à vista: marca-se uma conta de teste uma vez, ao
              criá-la, e a etiqueta no cabeçalho já diz o estado. Vale também
              para a própria conta - não tranca ninguém, só tira das contagens.
            */
            {
              key: 'teste',
              icon: <ExperimentOutlined />,
              label: pessoa.contaDeTeste ? 'Deixar de ser conta de teste' : 'Marcar como conta de teste',
              disabled: marcarTeste.isPending,
              onClick: () => marcarTeste.mutate(!pessoa.contaDeTeste),
            },
            ...(pessoa.suspensa
              ? []
              : [
                  {
                    key: 'suspender',
                    icon: <StopOutlined />,
                    label: 'Suspender conta',
                    disabled: ehVoce,
                    onClick: () => setSuspendendo(true),
                  },
                ]),
            { type: 'divider' as const },
            {
              key: 'excluir',
              icon: <DeleteOutlined />,
              label: 'Excluir conta',
              danger: true,
              disabled: ehVoce,
              onClick: () => setExcluindo(true),
            },
          ],
        }}
      >
        <Button>
          Ações <DownOutlined />
        </Button>
      </Dropdown>

      <Modal
        open={suspendendo}
        title={`Suspender a conta de ${pessoa.nome}?`}
        okText="Suspender"
        okButtonProps={{ danger: true, disabled: motivo.trim().length < 10 }}
        confirmLoading={suspender.isPending}
        cancelText="Cancelar"
        onOk={() => suspender.mutate()}
        onCancel={() => {
          setSuspendendo(false);
          setMotivo('');
        }}
        destroyOnHidden
      >
        <Typography.Paragraph>
          A conta para de entrar imediatamente e as sessões abertas caem. Nada é apagado, e
          reativar devolve tudo como estava - é a alternativa reversível a excluir.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          O motivo vai para a trilha de auditoria <strong>e</strong> é o que {pessoa.nome} lê ao
          tentar entrar. Escreva pensando nas duas leituras.
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          maxLength={300}
          showCount
          value={motivo}
          placeholder="Ex.: ritmo de leitura compatível com raspagem, apurado em 18/09."
          onChange={(evento) => setMotivo(evento.target.value)}
        />
      </Modal>

      <ExclusaoDaConta alvo={pessoa} aberto={excluindo} aoFechar={() => setExcluindo(false)} />
    </Flex>
  );
}
